import React, { useState, Fragment, useEffect, useLayoutEffect } from "react";
import { FormContainer } from "../../parts/form-container/FormContainer";
import { Button } from "../../parts/button/Button";
import { Progress } from "../../parts/progress/Progress";
import { FieldModel, createInitialFieldModel, areAllValid } from "../../shared/model/field-model";
import {
  createEmailValidator,
  createPasswordValidator,
  createCrossFieldEmailValidator,
  createCrossFieldPasswordValidator
} from "../../helpers/create-validators";
import {
  createCrossFieldHandlerForConfirmField,
  createCrossFieldHandlerForPrimaryField
} from "../../helpers/create-cross-field-handlers";
import { signUp } from "@simplyhealth/iam_client_lib";
import { ErrorMessages } from "../../parts/error-messages/ErrorMessages";
import { createElementName } from "../../shared/helpers/element.naming";
import { PageModel, createInitialPageModel, PageStatus } from "../../shared/model/page-model";
import { RegisterFieldSet } from "./RegisterFieldSet";
import { createSignUpRequest } from "./helpers/register-request-helper";
import { updatePageModelReducer } from "../../shared/model/page-model-reduers";
import { isProcessing, isCompleted, isErrored } from "../../helpers/display-block-logic";
import { fromQueryParameters } from "../../helpers/query-parameters-helper";
import { updatePageModelForStatusChangeReducer } from "./helpers/register-page-model-reducers";
import { FormFooter } from "../../parts/form-footer/FormFooter";
import { ANALYTICS_NAMES, sendPageVisitAnalytics } from "../../helpers/analytics-helpers";
import {createCodeDeliveryDto, CodeDeliveryDto} from "../../shared/model/user-model";
import {Redirect} from "react-router";
import { createUIMessage } from "../../helpers/response-error-message-handler"

export const Register = () => {
  const PAGE_NAME = "register";

  const [emailState, setEmailState] = useState<FieldModel>(createInitialFieldModel());
  const [confirmEmailState, setConfirmEmailState] = useState<FieldModel>(createInitialFieldModel());
  const [passwordState, setPasswordState] = useState<FieldModel>(createInitialFieldModel());
  const [confirmPasswordState, setConfirmPasswordState] = useState<FieldModel>(createInitialFieldModel());

  const [pageStatus, setPageStatus] = useState<PageStatus>("Ready");
  const [pageModel, setPageModel] = useState<PageModel>(createInitialPageModel());

  const [codeDeliveryDto, setCodeDeliveryDto] = useState<CodeDeliveryDto>();

  const [onEmailChange, onEmailBlur, invokeEmailValidation] = createCrossFieldHandlerForPrimaryField(
    emailState,
    confirmEmailState,
    setEmailState,
    setConfirmEmailState,
    createEmailValidator(),
    createCrossFieldEmailValidator()
  );

  const [
    onConfirmEmailChange,
    onConfirmEmailBlur,
    invokeConfirmEmailValidation
  ] = createCrossFieldHandlerForConfirmField(
    emailState,
    confirmEmailState,
    setConfirmEmailState,
    createCrossFieldEmailValidator()
  );
  const [onPasswordChange, onPasswordBlur, invokePasswordValidation] = createCrossFieldHandlerForPrimaryField(
    passwordState,
    confirmPasswordState,
    setPasswordState,
    setConfirmPasswordState,
    createPasswordValidator(),
    createCrossFieldPasswordValidator(),
    true // used for firing validation.
  );
  const [
    onConfirmPasswordChange,
    onConfirmPasswordBlur,
    invokeConfirmPasswordValidation
  ] = createCrossFieldHandlerForConfirmField(
    passwordState,
    confirmPasswordState,
    setConfirmPasswordState,
    createCrossFieldPasswordValidator()
  );

  useLayoutEffect(() => {
    if (!fromQueryParameters().doAllStandardExist()) {
      setPageStatus(() => "Missing Query Parameters");
    }
  }, []);

  useEffect(() => {
    document.title = "Register for online access | Simplyhealth";
  }, []);

  useEffect(() => {
    sendPageVisitAnalytics(ANALYTICS_NAMES.registration);
  }, []);

  useEffect(() => {
    setPageModel(pm => updatePageModelForStatusChangeReducer(pm, pageStatus));
  }, [pageStatus]);

  const onSubmitClick = () => {
    switch (pageStatus) {
      case "Ready":
        sendRequest();
        break;
      case "Sending":
        // Do nothing
        break;
      case "Succeeded":
        // Do nothing
        break;
      case "Failed":
        setPageStatus("Ready");
        break;
    }
  };

  const sendRequest = () => {
    invokeEmailValidation();
    invokeConfirmEmailValidation();
    invokePasswordValidation();
    invokeConfirmPasswordValidation();
    if (areAllFieldsComplete()) {
      setPageStatus("Sending");
      const request = createSignUpRequest(
        emailState.value,
        passwordState.value
      );
      signUp(request)
        .then(response => {
          // console.log("CONTINUE IN sendRequest");
          // console.log(response);
          if (response.status === "succeeded") {

            setCodeDeliveryDto(createCodeDeliveryDto(response.codeDeliveryMethod, response.codeDeliveryDestination,emailState.value.toLowerCase()));

            //const informationMessages = createRegisterSuccessMessages(response.codeDeliveryDestination);
            //setPageModel(updatePageModelReducer(pageModel, { informationMessages }));
            setPageStatus("Succeeded");
          } else {
            // console.log("Response is not succeeded");
            // console.log(response);
            // console.log(response?.message)
            errorReqHandler(response?.message);
          }
        })
        .catch(() => {
          errorReqHandler("");
        });
    }
  };

  const errorReqHandler = (errorMessage: string) => {
    let [informationMessages, title] = createUIMessage(errorMessage, "complete your registration");
    if (errorMessage) {
      informationMessages = [errorMessage];
    }
    setPageModel(updatePageModelReducer(pageModel, {informationMessages, title}));
    setPageStatus("Failed");
  };

  const areAllFieldsComplete = () => {
    return areAllValid(
      emailState,
      confirmEmailState,
      passwordState,
      confirmPasswordState
    );
  };

  return (
    <Fragment>
      <FormContainer
        pageName={PAGE_NAME}
        title={pageModel.title}
        messages={pageModel.informationMessages}
        image={<img src={pageModel.titleImage} alt="" />}
        fields={
          isProcessing(pageStatus) ? (
            <Fragment>
              <Progress />
            </Fragment>
          ) : isCompleted(pageStatus) ? (
              <Redirect to={{
                pathname: "/",
                state: codeDeliveryDto
              }}/>
          ) : isErrored(pageStatus) ? (
            <Fragment>
              <ErrorMessages
                name={createElementName(PAGE_NAME, "submit_errors", "errors")}
                messages={pageModel.errorMessages}
              />
            </Fragment>
          ) : (
            <RegisterFieldSet
              pageName={PAGE_NAME}
              emailState={emailState}
              onEmailChange={onEmailChange}
              onEmailBlur={onEmailBlur}
              confirmEmailState={confirmEmailState}
              onConfirmEmailChange={onConfirmEmailChange}
              onConfirmEmailBlur={onConfirmEmailBlur}
              passwordState={passwordState}
              onPasswordChange={onPasswordChange}
              onPasswordBlur={onPasswordBlur}
              confirmPasswordState={confirmPasswordState}
              onConfirmPasswordChange={onConfirmPasswordChange}
              onConfirmPasswordBlur={onConfirmPasswordBlur}
            />
          )
        }
        action={
          pageModel.showButton && (
            <Button pageName={PAGE_NAME} name="submit" caption={pageModel.buttonTitle} onClick={onSubmitClick} />
          )
        }
        footer={
          pageModel.showFooter && fromQueryParameters().doesRedirectUriExist() && <FormFooter message="Already have an account?" linkText="Login" redirectTo="/" />
        }
      />
    </Fragment>
  );
};
